import { HTML_EDITOR_ENGINE, isHtmlDocumentFile } from '@/utils/htmlFileDetection';

/**
 * 后端 NodeFile.editorEngine 字段语义。
 * 由 /json/research-model/node-* 系列接口返回，用于判断使用哪种编辑器引擎渲染文档。
 */

/** 引擎=1：石墨云文档，走 ShimoEditor + 云文档接口 */
export const EDITOR_ENGINE_SHIMO = 1;

/** 引擎=2：Tiptap + Yjs 协同（产品侧称"轻文档"），走 TiptapEditor + Hocuspocus */
export const EDITOR_ENGINE_TIPTAP = 2;

/** 引擎=4：ONLYOFFICE Presentation Editor，走 DocsAPI.DocEditor 渲染 PPT */
export const EDITOR_ENGINE_ONLYOFFICE = 4;

/** 引擎=5：AI 生成 HTML 可视化编辑，走 Y.Text(htmlSource) + 独立源沙箱 */
export const EDITOR_ENGINE_HTML = HTML_EDITOR_ENGINE;

/**
 * 判断 fileInfo 是否走 Tiptap + Yjs 协同（产品侧称之为「轻文档」）。
 * 真实后端返回 fileInfo.editorEngine === 2 表示该文档由 Tiptap + Yjs 渲染。
 * 命名按技术现实采用 "YJSDoc"：editor 是 Tiptap，但区分石墨/Tiptap 的本质语义是
 * 协同方案是否走 Yjs；与产品文案"轻文档"对应。
 *
 * @param {object | null | undefined} fileInfo - 文档信息，至少包含 editorEngine 字段
 * @returns {boolean}
 */
export function isYJSDocFile(fileInfo) {
  return fileInfo?.editorEngine === EDITOR_ENGINE_TIPTAP;
}

/**
 * 判断 fileInfo 是否走 ONLYOFFICE 渲染。
 * @param {object | null | undefined} fileInfo - 文档信息，至少包含 editorEngine 字段
 * @returns {boolean}
 */
export function isOnlyOfficeFile(fileInfo) {
  return fileInfo?.editorEngine === EDITOR_ENGINE_ONLYOFFICE;
}

/**
 * 图片类节点 fileType（与云文档 CLOUD_DOC_FILE_TYPE.IMAGE 一致，
 * 见 src/common/hooks/useCloudDoc.js openFile 中 `fileType === 5` 的图片预览分支）。
 */
export const FILE_TYPE_IMAGE = 5;

/**
 * 研究模式中间编辑器图片大图预览 z-index。
 * 需高于右侧面板 / 技能条 / AI 修改引导（4000）；Teleport 后由 CustomImagePreview 的全局 viewer 样式兜底。
 */
export const RESEARCH_IMAGE_PREVIEW_Z_INDEX = 5100;

/**
 * 中间编辑器图片预览支持的扩展名白名单（与左侧资料库 LOCAL_IMPORT_ACCEPT 中的图片格式对齐）。
 * 用于兜底判断：极少数后端节点未正确写入 fileType=5 时，仍可按扩展名识别为图片。
 */
const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'tif', 'tiff', 'webp', 'svg', 'bmp']);

/**
 * 判断 fileInfo 是否为可预览图片节点。
 *
 * 优先级：
 *   1. `fileType === FILE_TYPE_IMAGE`（5）—— 后端权威标识；
 *   2. `ext` 命中白名单 —— 兜底识别，避免极个别老节点缺少 fileType 时漏走图片分支。
 *
 * @param {object | null | undefined} fileInfo
 * @returns {boolean}
 */
export function isImageFile(fileInfo) {
  if (!fileInfo) return false;
  if (Number(fileInfo.fileType) === FILE_TYPE_IMAGE) return true;
  const ext = String(fileInfo.ext || '').toLowerCase();
  return IMAGE_EXTENSIONS.has(ext);
}

/**
 * 表格类节点 fileType（后端 SpreadJS 协同表格节点）。
 */
export const FILE_TYPE_TABLE = 10;

/**
 * SpreadJS 表格支持的扩展名白名单，用于兜底识别未正确写入 fileType=10 的老节点。
 */
const TABLE_EXTENSIONS = new Set(['xls', 'xlsx', 'xlsm']);

/**
 * 判断 fileInfo 是否为 SpreadJS 协同表格节点。
 *
 * 优先级：
 *   1. `fileType === FILE_TYPE_TABLE`（10）—— 后端权威标识；
 *   2. `ext` 命中白名单 —— 兜底识别。
 *
 * @param {object | null | undefined} fileInfo
 * @returns {boolean}
 */
export function isTableFile(fileInfo) {
  if (!fileInfo) return false;
  if (Number(fileInfo.fileType) === FILE_TYPE_TABLE) return true;
  const ext = String(fileInfo.ext || '').toLowerCase();
  return TABLE_EXTENSIONS.has(ext);
}

/**
 * AI 引用文件内容可读状态：0 进行中，1 成功，2 失败。
 * 字段来自 node-detail.aiReadableStatus；缺失时兼容历史数据，默认不拦截 AI 使用。
 */
/** 文件内容仍在解析中，暂不可被 AI 读取。 */
export const AI_READABLE_STATUS_PARSING = 0;
/** 文件内容解析成功，可被 AI 读取。 */
export const AI_READABLE_STATUS_SUCCESS = 1;
/** 文件内容解析失败，暂不可被 AI 读取。 */
export const AI_READABLE_STATUS_FAILED = 2;

/**
 * 获取文件无法用于 AI 对话的原因。
 * @param {object | null | undefined} fileInfo
 * @returns {'parsing' | 'failed' | ''}
 */
export function getAiUnreadableReason(fileInfo) {
  const status = Number(fileInfo?.aiReadableStatus);
  if (status === AI_READABLE_STATUS_PARSING) return 'parsing';
  if (status === AI_READABLE_STATUS_FAILED) return 'failed';
  return '';
}
/**
 * PPT 类节点 fileType（与云文档 CLOUD_DOC_FILE_TYPE.PPT 一致）。
 */
export const FILE_TYPE_PPT = 3;

/**
 * PPT 支持的扩展名白名单，用于兜底识别未正确写入 fileType=3 的老节点。
 */
const PPT_EXTENSIONS = new Set(['ppt', 'pptx']);

/**
 * 判断 fileInfo 是否为 PPT 节点。
 *
 * 优先级：
 *   1. `fileType === FILE_TYPE_PPT`（3）—— 后端权威标识；
 *   2. `ext` 命中白名单 —— 兜底识别。
 *
 * @param {object | null | undefined} fileInfo
 * @returns {boolean}
 */
export function isPptFile(fileInfo) {
  if (!fileInfo) return false;
  if (Number(fileInfo.fileType) === FILE_TYPE_PPT) return true;
  const ext = String(fileInfo.ext || '').toLowerCase();
  return PPT_EXTENSIONS.has(ext);
}

/**
 * 判断 fileInfo 是否为 HTML 可视化预览文档。
 * 与 htmlFileDetection 一致：editorEngine 明确时以其为准；缺失时才回退 fileType/扩展名。
 * @param {object | null | undefined} fileInfo
 * @returns {boolean}
 */
export function isHtmlIframeFile(fileInfo) {
  return isHtmlDocumentFile(fileInfo);
}

const MARKDOWN_EXTENSIONS = new Set(['md', 'markdown']);

/**
 * 判断 fileInfo 是否为 Markdown 节点。
 * @param {object | null | undefined} fileInfo
 * @returns {boolean}
 */
export function isMarkdownFile(fileInfo) {
  if (!fileInfo) return false;
  const ext = String(fileInfo.ext || '').toLowerCase();
  if (MARKDOWN_EXTENSIONS.has(ext)) return true;
  const fileName = String(fileInfo.fileName || fileInfo.name || '').toLowerCase();
  // 从文件名末尾提取扩展名，兜底识别未写入 ext 的 Markdown 节点
  const matched = fileName.match(/\.([a-z0-9]+)$/);
  return matched ? MARKDOWN_EXTENSIONS.has(matched[1]) : false;
}
